import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { translations, type Lang, type TranslationKey } from "./translations";
import { interpolate } from "@/lib/interpolate";

/**
 * Lightweight i18n.
 *
 * - First load: read localStorage, else Romanian (the default for new visitors;
 *   they can switch to English and that choice is stored).
 * - User toggles: persist to localStorage so refreshes keep the choice.
 * - `t(key, vars)` substitutes `{name}` placeholders in the template string.
 * - Falls back to the EN string if a key is missing in the target locale.
 */

const STORAGE_KEY = "resto_lang";

interface I18nContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * Resolve the visitor's language without React context — also used by the
 * ErrorBoundary (which renders outside the provider) and mirrored by the
 * static shell bootstrap in index.html.
 */
export function detectInitialLang(): Lang {
  if (typeof window === "undefined") return "ro";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "ro") return stored;
  } catch {
    /* localStorage may be blocked — fall through to the default. */
  }
  return "ro";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => detectInitialLang());

  // Reflect the active language on the <html> tag for browser features like
  // hyphenation, screen readers, and CSS :lang() selectors.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* Storage write may fail in private mode — non-fatal. */
    }
  }, []);

  const t = useMemo(() => {
    const dict = translations[lang];
    return (key: TranslationKey, vars?: Record<string, string | number>) => {
      const template = dict[key] ?? translations.en[key] ?? key;
      return interpolate(template, vars);
    };
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useTranslation must be used inside <I18nProvider>");
  }
  return ctx;
}
